import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams, Link, useOutletContext, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../../common/state/hooks";
import { useLoading } from "../../common/components/AppState/IsLoading";
import { LoadingState } from "../../common/state/slice/appStateSlice";
import {
  fetchEnduserSessionDetailsForGivenProject,
  fetchProjectGroupsCompletion,
  fetchCurrentProjectGroupCompletion,
  getEnduserSessionDetailForGivenProject,
  getEnduserCurrentProjectGroupCompletion,
  getEnduserProjectGroupsCompletion,
} from "../../features/enduserStats/state/enduserStatsSlice";
import {
  Pagination,
  EmptyState,
  SearchAndSort,
  SortOption,
  SessionCard,
  ScoreGaugeCard,
  EngagementCard,
  TopInteractionsCard,
  ExportButton,
} from "../../features/statsNew/components";
import { exportSessionsToCsv } from "../../features/statsNew/utils/exportCsv";
import {
  useModuleStats,
  useGazeHeatmapCheck,
  useEnduserGraphStats,
  useEnduserScorePerScene,
} from "../../features/statsNew/hooks";
import SessionDetailModal from "../../features/statsNew/components/modals/SessionDetailModal";
import { InteractionAttemptsModal } from "../../features/statsNew/components/modals";
import { DateRange } from "../../features/statsNew/components/DateRangePicker";
import {
  SuccessRatePerSceneChart,
  StatsGraph,
  EnduserInteractionAnalysisTable,
} from "../../features/statsNew/components/charts";

const ENDUSER_MODULE_DETAIL_COMPONENT_ID = "enduserModuleDetailView";

interface OutletContextType {
  dateRange: DateRange;
  clearDateRange: () => void;
}

type TabKey = "stats" | "sessions";

const EnduserModuleDetailView = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { parcoursId, moduleId } = useParams<{ parcoursId: string; moduleId: string }>();
  const { dateRange, clearDateRange } = useOutletContext<OutletContextType>();
  const [searchParams, setSearchParams] = useSearchParams();

  const projectGroupsCompletion = useSelector(getEnduserProjectGroupsCompletion);
  const currentProjectGroupCompletion = useSelector(getEnduserCurrentProjectGroupCompletion);
  const sessionDetail = useSelector(getEnduserSessionDetailForGivenProject);
  const loadingState = useLoading(ENDUSER_MODULE_DETAIL_COMPONENT_ID);
  const isLoading = loadingState === LoadingState.Loading;

  // Read sessionId from URL query parameter
  const sessionIdFromUrl = searchParams.get("sessionId");

  const [activeTab, setActiveTab] = useState<TabKey>(sessionIdFromUrl ? "sessions" : "stats");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [sortKey, setSortKey] = useState("date-desc");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(
    sessionIdFromUrl ? Number(sessionIdFromUrl) : null,
  );
  const [selectedSceneId, setSelectedSceneId] = useState<number | string | null>(null);
  const [selectedSceneName, setSelectedSceneName] = useState<string | null>(null);
  const [selectedInteraction, setSelectedInteraction] = useState<{
    contentId: number;
    contentName: string;
    contentType: string;
  } | null>(null);
  // Track if scene selection came from the graph (to avoid auto-focus when clicking on graph)
  const sceneSelectedFromGraphRef = useRef(false);

  // Find parcours info
  const parcoursInfo = useMemo(() => {
    return projectGroupsCompletion.find((pg) => pg.projectGroupId === Number(parcoursId));
  }, [projectGroupsCompletion, parcoursId]);

  // Find module info
  const moduleInfo = useMemo(() => {
    return currentProjectGroupCompletion.find((m) => m.projectId === Number(moduleId));
  }, [currentProjectGroupCompletion, moduleId]);

  // Fetch graph stats for the module structure visualization (enduser-specific)
  const graphStats = useEnduserGraphStats({
    projectId: Number(moduleId),
    startDate: dateRange.startDate?.toISOString(),
    endDate: dateRange.endDate?.toISOString(),
  });

  // Fetch scene scores (enduser-specific)
  const { data: scorePerScene, isLoading: isScoreDataLoading } = useEnduserScorePerScene({
    projectId: Number(moduleId),
    startDate: dateRange.startDate?.toISOString(),
    endDate: dateRange.endDate?.toISOString(),
  });

  // Extract interaction stats for TopInteractionsCard
  const topInteractionsData = useMemo(() => {
    if (!graphStats.nodes || graphStats.nodes.length === 0) return [];

    // Filter assets (interactions) that have stats data with valid successRate
    // Stats are only present when there are actual attempts
    return graphStats.nodes
      .filter(
        (node): node is typeof node & { stats: NonNullable<typeof node.stats> } =>
          node.type === "asset" && node.stats != null && typeof node.stats.successRate === "number",
      )
      .map((node) => ({
        contentId: node.contentId,
        contentName: node.name,
        successRate: node.stats.successRate,
        attemptCount: node.stats.attemptCount,
      }));
  }, [graphStats.nodes]);

  useEffect(() => {
    if (moduleId) {
      dispatch(
        fetchEnduserSessionDetailsForGivenProject({
          componentId: ENDUSER_MODULE_DETAIL_COMPONENT_ID,
          projectId: Number(moduleId),
        }),
      );
    }
  }, [dispatch, moduleId]);

  // Fetch projectGroupsCompletion if empty (direct navigation)
  useEffect(() => {
    if (projectGroupsCompletion.length === 0) {
      dispatch(
        fetchProjectGroupsCompletion({
          componentId: ENDUSER_MODULE_DETAIL_COMPONENT_ID,
          limit: -1,
        }),
      );
    }
  }, [dispatch, projectGroupsCompletion.length]);

  // Fetch currentProjectGroupCompletion when we have parcoursInfo
  // Use buildId as dependency (primitive) instead of parcoursInfo (object) to avoid double fetch
  const buildId = parcoursInfo?.latestVersion?.buildId;
  useEffect(() => {
    if (buildId && currentProjectGroupCompletion.length === 0) {
      dispatch(
        fetchCurrentProjectGroupCompletion({
          componentId: ENDUSER_MODULE_DETAIL_COMPONENT_ID,
          buildId,
        }),
      );
    }
  }, [dispatch, buildId, currentProjectGroupCompletion.length]);

  const sortOptions: SortOption[] = [
    { key: "date-desc", label: t("statsNew.sort.activityRecent") },
    { key: "date-asc", label: t("statsNew.sort.activityOldest") },
    { key: "score-desc", label: t("statsNew.sort.scoreHigh") },
    { key: "score-asc", label: t("statsNew.sort.scoreLow") },
  ];

  // Session type for filtering
  interface FilterableSession {
    isCompleted?: boolean;
    isPerfectScore?: boolean;
  }

  // Filter chips configuration
  type FilterChip = {
    id: string;
    label: string;
    filter: (session: FilterableSession) => boolean;
  };

  const filterChips: FilterChip[] = [
    {
      id: "completed",
      label: t("statsNew.filters.completed"),
      filter: (s) => s.isCompleted === true,
    },
    {
      id: "perfect",
      label: t("statsNew.filters.perfectScore"),
      filter: (s) => s.isPerfectScore === true,
    },
  ];

  const toggleFilter = useCallback((filterId: string) => {
    setActiveFilters((prev) =>
      prev.includes(filterId) ? prev.filter((f) => f !== filterId) : [...prev, filterId],
    );
    setCurrentPage(0);
  }, []);

  // Transform sessions to match expected format for useModuleStats and SessionCard
  const transformedSessions = useMemo(() => {
    if (!sessionDetail?.sessions) return [];

    return sessionDetail.sessions.map((session) => {
      // Parse score string like "3/4" to get numeric values
      let score: number | null = null;
      let scoreMax: number | null = null;
      if (session.score) {
        const parts = session.score.split("/");
        if (parts.length === 2) {
          score = parseInt(parts[0], 10);
          scoreMax = parseInt(parts[1], 10);
        }
      }

      return {
        id: session.id,
        createdAt: session.createdAt,
        score,
        score_max: scoreMax,
        total_session_duration: session.duration,
        total_session_completion_percentage: session.progressPercentage,
        is_completed: session.progressPercentage === 100,
        is_succeeded: session.isPerfectScore,
        isPerfectScore: session.isPerfectScore,
        isCompleted: session.progressPercentage === 100,
        enduser: session.enduser,
        target_platform: session.target_platform,
        device_model: session.device_model,
      };
    });
  }, [sessionDetail?.sessions]);

  // Filter sessions by date range
  const filteredSessions = useMemo(() => {
    return transformedSessions.filter((session) => {
      const sessionDate = new Date(session.createdAt);
      if (dateRange.startDate && sessionDate < dateRange.startDate) return false;
      if (dateRange.endDate && sessionDate > dateRange.endDate) return false;
      return true;
    });
  }, [transformedSessions, dateRange]);

  // Compute module stats using custom hook
  const moduleStats = useModuleStats(filteredSessions);

  // Check which sessions have gaze heatmap data available
  const sessionIdsForGazeCheck = useMemo(
    () => filteredSessions.map((s) => s.id),
    [filteredSessions],
  );
  const { gazeDataAvailability } = useGazeHeatmapCheck(sessionIdsForGazeCheck);

  // Filter and sort sessions for the sessions tab
  const displaySessions = useMemo(() => {
    let result = filteredSessions;

    // Apply search filter (by user name)
    if (searchValue.trim()) {
      const query = searchValue.toLowerCase().trim();
      result = result.filter((session) => {
        const userName = session.enduser?.username || session.enduser?.firstname || "";
        return userName.toLowerCase().includes(query);
      });
    }

    // Apply active filter chips
    if (activeFilters.length > 0) {
      result = result.filter((session) =>
        activeFilters.every((filterId) => {
          const chip = filterChips.find((c) => c.id === filterId);
          return chip ? chip.filter(session) : true;
        }),
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortKey) {
        case "date-desc":
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case "date-asc":
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case "score-desc":
          return (b.score || 0) - (a.score || 0);
        case "score-asc":
          return (a.score || 0) - (b.score || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [filteredSessions, searchValue, sortKey, activeFilters, filterChips]);

  // Reset to first page when search or sort changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue, sortKey, dateRange, activeFilters]);

  // Paginate sessions
  const paginatedSessions = useMemo(() => {
    const start = currentPage * pageSize;
    return displaySessions.slice(start, start + pageSize);
  }, [displaySessions, currentPage, pageSize]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleExport = async () => {
    const locale = i18n.language === "fr" ? "fr" : "en";
    const moduleName = moduleInfo?.projectName?.replace(/[^a-zA-Z0-9]/g, "-") || "module";
    await exportSessionsToCsv(displaySessions, {
      filename: `sessions-${moduleName}`,
      locale,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500" />
      </div>
    );
  }

  if (!moduleInfo) {
    return (
      <EmptyState
        icon="folder"
        title={t("statsNew.module.notFound")}
        description={t("statsNew.emptyState.noModuleDescription")}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <nav className="stats-breadcrumb mb-4">
        <Link to="/stats/parcours">{t("enduserStats.tabs.parcours")}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <Link to={`/stats/parcours/${parcoursId}`}>{parcoursInfo?.projectGroupName}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <span className="stats-breadcrumb-current">{moduleInfo?.projectName}</span>
      </nav>

      {/* Header */}
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">{moduleInfo.projectName}</h2>
        <p className="text-sm text-gray-500 mt-1">
          {moduleStats.totalSessions} {t("statsNew.session.sessions")}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => setActiveTab("stats")}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeTab === "stats" ? "text-green-600" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {t("statsNew.module.statsTab")}
          {activeTab === "stats" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
          )}
        </button>
        <button
          onClick={() => setActiveTab("sessions")}
          className={`px-6 py-3 text-sm font-medium transition-colors relative ${
            activeTab === "sessions" ? "text-green-600" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {t("statsNew.module.sessionsTab")}
          {activeTab === "sessions" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
          )}
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "stats" && (
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Zone 1: Hero Metrics - 2 cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ScoreGaugeCard
              avgScore={moduleStats.avgScore}
              maxScore={moduleStats.avgMaxScore ?? undefined}
              bestScore={moduleStats.bestScore}
              worstScore={moduleStats.worstScore}
            />
            <EngagementCard
              totalSessions={moduleStats.totalSessions}
              completionRate={moduleStats.completionRate}
              avgProgressionPercent={moduleStats.avgProgressionPercent}
              avgDuration={moduleStats.avgDuration}
              abandonedSessions={moduleStats.abandonedSessions}
            />
          </div>

          {/* Zone 2: Module Structure Graph */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-500">{t("statsNew.graph.title")}</h3>
              <p className="text-xs text-gray-400">{t("statsNew.charts.clickToFilter")}</p>
            </div>
            <StatsGraph
              nodes={graphStats.nodes}
              links={graphStats.links}
              layout={graphStats.layout}
              isLoading={graphStats.isLoading}
              error={graphStats.error}
              height={400}
              focusNodeId={
                sceneSelectedFromGraphRef.current
                  ? null
                  : selectedSceneId != null
                  ? Number(selectedSceneId)
                  : null
              }
              onSceneClick={(node) => {
                sceneSelectedFromGraphRef.current = true;
                if (selectedSceneId === node.contentId) {
                  setSelectedSceneId(null);
                  setSelectedSceneName(null);
                } else {
                  setSelectedSceneId(node.contentId);
                  setSelectedSceneName(node.name);
                }
              }}
              onInteractionClick={(node) => {
                setSelectedInteraction({
                  contentId: node.contentId,
                  contentName: node.name,
                  contentType: node.modelName,
                });
              }}
            />
          </div>

          {/* Zone 3: Detailed Analysis - 2 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Performance per scene */}
            <div className="lg:col-span-2 stats-chart-container">
              <h3 className="text-sm font-medium text-gray-500">
                {t("statsNew.charts.successRatePerScene")}
              </h3>
              <p className="text-xs text-gray-400 mb-2">{t("statsNew.charts.clickToFilter")}</p>
              <SuccessRatePerSceneChart
                data={scorePerScene}
                isLoading={isScoreDataLoading}
                selectedSceneId={selectedSceneId}
                onSceneClick={(contentId, sceneName) => {
                  sceneSelectedFromGraphRef.current = false;
                  if (selectedSceneId === contentId) {
                    setSelectedSceneId(null);
                    setSelectedSceneName(null);
                  } else {
                    setSelectedSceneId(contentId);
                    setSelectedSceneName(sceneName);
                  }
                }}
              />
            </div>

            {/* Top Interactions - Quick insights */}
            <TopInteractionsCard
              interactions={topInteractionsData}
              isLoading={graphStats.isLoading}
              onInteractionClick={(contentId, contentName) => {
                // Find the interaction type from graph nodes
                const node = graphStats.nodes.find((n) => n.contentId === contentId);
                setSelectedInteraction({
                  contentId,
                  contentName,
                  contentType: node?.modelName || "",
                });
              }}
            />
          </div>

          {/* Interaction Analysis Table */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("statsNew.interactions.title")}
            </h3>
            <EnduserInteractionAnalysisTable
              projectId={Number(moduleId)}
              startDate={dateRange.startDate?.toISOString()}
              endDate={dateRange.endDate?.toISOString()}
              filterSceneId={selectedSceneId}
              filterSceneName={selectedSceneName}
              onClearFilter={() => {
                sceneSelectedFromGraphRef.current = false;
                setSelectedSceneId(null);
                setSelectedSceneName(null);
              }}
              onSceneClick={(contentId, sceneName) => {
                sceneSelectedFromGraphRef.current = false;
                setSelectedSceneId(contentId);
                setSelectedSceneName(sceneName);
              }}
              onInteractionClick={(contentId, contentName, contentType) => {
                setSelectedInteraction({ contentId, contentName, contentType });
              }}
            />
          </div>
        </div>
      )}

      {/* Sessions Tab Content */}
      {activeTab === "sessions" && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Search, Sort and Export */}
          <div className="flex items-center justify-between gap-4 mb-3">
            <SearchAndSort
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              searchPlaceholder={t("statsNew.session.searchPlaceholder")}
              sortOptions={sortOptions}
              selectedSort={sortKey}
              onSortChange={setSortKey}
            />
            <ExportButton onExport={handleExport} disabled={displaySessions.length === 0} />
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {filterChips.map((chip) => (
              <button
                key={chip.id}
                onClick={() => toggleFilter(chip.id)}
                className={`filter-chip ${activeFilters.includes(chip.id) ? "active" : ""}`}
              >
                {chip.label}
              </button>
            ))}
            {activeFilters.length > 0 && (
              <button
                onClick={() => setActiveFilters([])}
                className="text-sm text-gray-500 hover:text-gray-700 ml-2 underline"
              >
                {t("statsNew.filters.clearAll")}
              </button>
            )}
          </div>

          {/* Results counter */}
          <p className="text-sm text-gray-500 mb-3">
            {displaySessions.length} session{displaySessions.length !== 1 ? "s" : ""}{" "}
            {t("statsNew.filters.found")}
          </p>

          {/* Sessions list */}
          {displaySessions.length === 0 ? (
            // Check if no results due to date filter
            (dateRange.startDate || dateRange.endDate) && transformedSessions.length > 0 ? (
              <EmptyState
                icon="calendar"
                title={t("statsNew.emptyState.noData")}
                description={t("statsNew.emptyState.noDataDescription")}
                action={{
                  label: t("statsNew.emptyState.viewAll"),
                  onClick: clearDateRange,
                }}
              />
            ) : (
              <EmptyState
                icon="calendar"
                title={t("statsNew.session.noSessions")}
                description={t("statsNew.emptyState.noSessionsDescription")}
              />
            )
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                {paginatedSessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    id={session.id}
                    date={new Date(session.createdAt)}
                    score={session.score}
                    maxScore={session.score_max}
                    duration={session.total_session_duration ?? null}
                    progressionPercent={session.total_session_completion_percentage ?? null}
                    isCompleted={session.is_completed || false}
                    isSucceeded={session.is_succeeded || false}
                    userName={session.enduser?.username || session.enduser?.firstname}
                    userAvatarUrl={session.enduser?.source_avatar?.s3_url}
                    onClick={() => {
                      setSelectedSessionId(session.id);
                    }}
                    averageScore={moduleStats.avgScore}
                    hasGazeData={gazeDataAvailability[session.id] ?? false}
                    targetPlatform={session.target_platform}
                    deviceModel={session.device_model}
                  />
                ))}
              </div>
              <div className="flex-shrink-0 mt-auto">
                <Pagination
                  currentPage={currentPage}
                  totalItems={displaySessions.length}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Session Detail Modal */}
      {selectedSessionId !== null && (
        <SessionDetailModal
          sessionId={selectedSessionId}
          session={transformedSessions.find((s) => s.id === selectedSessionId)}
          projectId={Number(moduleId)}
          onClose={() => {
            setSelectedSessionId(null);
            // Remove sessionId from URL when closing modal
            if (searchParams.has("sessionId")) {
              searchParams.delete("sessionId");
              setSearchParams(searchParams, { replace: true });
            }
          }}
        />
      )}

      {/* Interaction Attempts Modal */}
      {selectedInteraction && (
        <InteractionAttemptsModal
          contentId={selectedInteraction.contentId}
          contentName={selectedInteraction.contentName}
          contentType={selectedInteraction.contentType}
          projectId={Number(moduleId)}
          startDate={dateRange.startDate?.toISOString()}
          endDate={dateRange.endDate?.toISOString()}
          onClose={() => setSelectedInteraction(null)}
        />
      )}
    </div>
  );
};

export default EnduserModuleDetailView;
